import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, RotateCcw, Clock, Upload, Edit3, Loader } from 'lucide-react';
import { documentService } from '../../services/api';
import useModal from '../../hooks/useModal';
import ConfirmDialog from '../common/ConfirmDialog';

const VersionHistory = ({ document, isOpen, onClose, onRestore }) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);
  const [versions, setVersions] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [currentVersionNumber, setCurrentVersionNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  const loadVersions = useCallback(async () => {
    if (!document?.id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await documentService.getVersions(document.id);
      setVersions(response.data.versions || []);
      setImportHistory(response.data.import_history || []);
      setCurrentVersionNumber(response.data.current_version_number || 1);
    } catch (err) {
      console.error('Failed to load version history:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [document]);

  useEffect(() => {
    if (isOpen && document) {
      loadVersions();
    }
    return () => {
      setVersions([]);
      setImportHistory([]);
      setError(null);
    };
  }, [isOpen, document, loadVersions]);

  const handleDownloadVersion = async (version) => {
    try {
      const response = await documentService.downloadVersion(document.id, version.id);
      window.open(response.data.download_url, '_blank');
    } catch (err) {
      console.error('Failed to download version:', err);
    }
  };

  const handleRestore = (version) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Restore Version',
      message: `Restore version ${version.version_number}? The current file will be saved as a new version.`,
      variant: 'warning',
      confirmText: 'Restore',
      onConfirm: async () => {
        try {
          setRestoringId(version.id);
          await documentService.restoreVersion(document.id, version.id);
          await loadVersions();
          if (onRestore) onRestore();
        } catch (err) {
          console.error('Failed to restore version:', err);
          setError('Failed to restore version');
        } finally {
          setRestoringId(null);
        }
      },
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'collabora': return <Edit3 className="h-3.5 w-3.5" />;
      case 'upload': return <Upload className="h-3.5 w-3.5" />;
      case 'restore': return <RotateCcw className="h-3.5 w-3.5" />;
      default: return <Clock className="h-3.5 w-3.5" />;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'collabora': return 'Edited in browser';
      case 'upload': return 'File replaced';
      case 'restore': return 'Auto-saved before restore';
      default: return source;
    }
  };

  if (!isOpen || !document) return null;

  return (
    <>
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white h-full w-full max-w-md shadow-xl flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Version History</h2>
            <p className="text-sm text-gray-500 truncate" title={document.original_file_name}>
              {document.original_file_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Current version indicator */}
        <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-100">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-200 text-yellow-800">
              Current: v{currentVersionNumber}
            </span>
            <span className="text-xs text-gray-500">
              {formatFileSize(document.file_size)}
            </span>
          </div>
        </div>

        {/* Version list */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading versions...</span>
            </div>
          ) : error ? (
            <div className="p-4 text-center">
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={loadVersions}
                className="mt-2 text-sm text-yellow-600 hover:text-yellow-800"
              >
                Try again
              </button>
            </div>
          ) : versions.length === 0 && importHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <Clock className="h-8 w-8 text-gray-300 mb-2" />
              <p className="text-sm">No previous versions</p>
              <p className="text-xs text-gray-400 mt-1">Versions are created when files are edited or replaced</p>
            </div>
          ) : (
            <>
            <div className="divide-y divide-gray-100">
              {versions.map((version) => (
                <div key={version.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          v{version.version_number}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          {getSourceIcon(version.created_by_source)}
                          {getSourceLabel(version.created_by_source)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {formatDate(version.created_at)}
                        {version.created_by_name && ` by ${version.created_by_name}`}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatFileSize(version.file_size)}
                      </p>
                      {version.change_description && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                          {version.change_description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button
                        onClick={() => handleDownloadVersion(version)}
                        className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                        title="Download this version"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleRestore(version)}
                        disabled={restoringId === version.id}
                        className="p-1.5 text-gray-400 hover:text-yellow-700 hover:bg-yellow-50 rounded transition-colors disabled:opacity-50"
                        title="Restore this version"
                      >
                        {restoringId === version.id ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCcw className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Import history — metadata-only versions from MyCase */}
            {importHistory.length > 0 && (
              <div className="border-t border-gray-200">
                <div className="px-4 py-2 bg-gray-50">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    MyCase History (file not available)
                  </p>
                </div>
                <div className="divide-y divide-gray-100">
                  {importHistory.map((entry, idx) => (
                    <div key={idx} className="px-4 py-3 opacity-70">
                      <div className="flex-1 min-w-0">
                        {entry.file_name ? (
                          <>
                            <p className="text-xs font-medium text-gray-700 truncate" title={entry.file_name}>
                              {entry.file_name}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {entry.uploaded_at && entry.uploaded_at}
                              {entry.uploaded_by && ` by ${entry.uploaded_by}`}
                            </p>
                            {entry.mime_type && (
                              <p className="text-xs text-gray-400 mt-0.5">{entry.mime_type}</p>
                            )}
                          </>
                        ) : entry.raw ? (
                          <p className="text-xs text-gray-500">{entry.raw}</p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
    <ConfirmDialog
      isOpen={confirmDialog.isOpen}
      onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      onConfirm={confirmDialog.onConfirm}
      title={confirmDialog.title}
      message={confirmDialog.message}
      variant={confirmDialog.variant}
      confirmText={confirmDialog.confirmText}
    />
    </>
  );
};

export default VersionHistory;
